import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
  CircularProgress,
  FormControlLabel,
  Switch,
  CardActions,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Chip,
  Snackbar,
  Alert
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';

import { createCategory, getAllCategories, updateCategory, deleteCategory, getCategoryOptions, createCategoryOption, updateCategoryOption, deleteCategoryOption } from '../Apis/CategoriesApis';

const Categories = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newCategory, setNewCategory] = useState({
    name: '',
    image: null,
    logo: null,
    isOption: false,
    BoxText: '',
    isActive: true
  });
  const [editingId, setEditingId] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [previews, setPreviews] = useState({ image: null, logo: null });
  const [options, setOptions] = useState([]);
  const [tempOption, setTempOption] = useState('');
  const [tempPriceTag, setTempPriceTag] = useState('');
  const [isOptionActionLoading, setIsOptionActionLoading] = useState(false);
  const [editingOption, setEditingOption] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null); // { type: 'category' | 'option', data: any }
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState(null);

  const fetchCategories = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getAllCategories();
      if (response.data && response.data.status) {
        setCategories(response.data.categories || []);
      } else {
        setError(response.data.message || "Failed to load categories.");
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      setError("Failed to load categories. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchOptions = async (categoryId) => {
    try {
      const response = await getCategoryOptions(categoryId);
      if (response.data && response.data.status) {
        setOptions(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching options:", error);
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setNewCategory({ name: '', image: null, logo: null, isOption: false, BoxText: '', isActive: true });
    setPreviews({ image: null, logo: null });
    setEditingId(null);
    setOptions([]);
    setTempOption('');
    setTempPriceTag('');
    setEditingOption(null);
  };

  const handleMenuOpen = (event, category) => {
    setAnchorEl(event.currentTarget);
    setSelectedCategory(category);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCategory(null);
  };

  const handleEdit = () => {
    const category = selectedCategory;
    setEditingId(category.id);
    setNewCategory({
      name: category.name,
      image: null,
      logo: null,
      isOption: category.isOption,
      BoxText: category.BoxText || '',
      isActive: category.isActive !== undefined ? category.isActive : true
    });
    setPreviews({
      image: category.image,
      logo: category.logo
    });
    if (category.isOption) {
      fetchOptions(category.id);
    } else {
      setOptions([]);
    }
    handleMenuClose();
    setOpen(true);
  };


  const handleAddOption = async () => {
    if (!tempOption.trim()) return;

    if (editingOption) {
      if (editingId && !editingOption.isLocal) {
        // Hit API immediately if editing an existing option
        setIsOptionActionLoading(true);
        try {
          const formData = new FormData();
          formData.append('optionName', tempOption);
          const response = await updateCategoryOption(editingOption.id, formData);
          if (response.data && response.data.status) {
            fetchOptions(editingId);
            setTempOption('');
            setEditingOption(null);
            showSnackbar("Option updated successfully");
          } else {
            showSnackbar(response.data.message || "Failed to update option", 'error');
          }
        } catch (error) {
          console.error("Error updating option:", error);
          showSnackbar("Something went wrong while updating option.", 'error');
        } finally {
          setIsOptionActionLoading(false);
        }
      } else {
        // Update local list
        setOptions(options.map(opt => opt === editingOption ? { ...opt, optionName: tempOption } : opt));
        setTempOption('');
        setEditingOption(null);
      }
      return;
    }

    if (editingId) {
      setIsOptionActionLoading(true);
      try {
        const formData = new FormData();
        formData.append('categoryId', editingId);
        formData.append('optionName', tempOption);
        const response = await createCategoryOption(formData);
        if (response.data && response.data.status) {
          fetchOptions(editingId);
          setTempOption('');
          showSnackbar("Option added successfully");
        } else {
          showSnackbar(response.data.message || "Failed to add option", 'error');
        }
      } catch (error) {
        console.error("Error adding option:", error);
        showSnackbar("Something went wrong while adding option.", 'error');
      } finally {
        setIsOptionActionLoading(false);
      }
    } else {
      setOptions([...options, { optionName: tempOption, isLocal: true }]);
      setTempOption('');
    }
  };

  const handleStartEditOption = (option) => {
    setEditingOption(option);
    setTempOption(option.optionName || option.name);
  };

  const handleDeleteOption = (option) => {
    if (editingId && !option.isLocal) {
      setItemToDelete({ type: 'option', data: option });
      setDeleteDialogOpen(true);
    } else {
      setOptions(options.filter(o => o !== option));
      if (editingOption === option) {
        setEditingOption(null);
        setTempOption('');
      }
    }
  };

  const handleChange = (e) => {
    const { name, value, files, checked, type } = e.target;

    if (type === 'file') {
      const file = files[0];
      setNewCategory(prev => ({ ...prev, [name]: file }));
      if (file) {
        setPreviews(prev => ({ ...prev, [name]: URL.createObjectURL(file) }));
      } else {
        setPreviews(prev => ({ ...prev, [name]: null }));
      }
    } else {
      setNewCategory(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('name', newCategory.name);
      if (newCategory.image) formData.append('image', newCategory.image);
      if (newCategory.logo) formData.append('logo', newCategory.logo);
      formData.append('isOption', newCategory.isOption);
      
      formData.append('BoxText', newCategory.BoxText);
      formData.append('isActive', newCategory.isActive);

      let response;
      if (editingId) {
        response = await updateCategory(editingId, formData);
      } else {
        response = await createCategory(formData);
      }

      if (response.data && response.data.status) {
        if (!editingId && newCategory.isOption && options.length > 0) {
          const categoryId = response.data.id || response.data.data?.id || response.data.category?.id;
          if (categoryId) {
            for (const opt of options) {
              const optFormData = new FormData();
              optFormData.append('categoryId', categoryId);
              optFormData.append('optionName', opt.optionName);
              await createCategoryOption(optFormData).catch(err => console.error("Error adding option during creation:", err));
            }
          }
        }

        setOpen(false);
        setNewCategory({ name: '', image: null, logo: null, isOption: false, BoxText: '', isActive: true });
        setPreviews({ image: null, logo: null });
        setOptions([]);
        setTempOption('');
        setTempOption('');
        setEditingOption(null);
        setEditingId(null);
        fetchCategories();
        showSnackbar(`Category ${editingId ? 'updated' : 'created'} successfully`);
      } else {
        showSnackbar(response.data.message || "Something went wrong! Please try again.", 'error');
      }
    } catch (error) {
      console.error("Error saving category:", error);
      showSnackbar(error.response?.data?.message || "Failed to save category. Please check your connection.", 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = () => {
    setItemToDelete({ type: 'category', data: selectedCategory });
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;

    setIsDeleting(true);
    try {
      if (itemToDelete.type === 'category') {
        const response = await deleteCategory(itemToDelete.data.id);
        if (response.data && response.data.status) {
          fetchCategories();
          handleMenuClose();
          showSnackbar("Category deleted successfully");
          setDeleteDialogOpen(false);
        } else {
          showSnackbar(response.data.message || "Failed to delete category", 'error');
        }
      } else if (itemToDelete.type === 'option') {
        const response = await deleteCategoryOption(itemToDelete.data.id);
        if (response.data && response.data.status) {
          fetchOptions(editingId);
          showSnackbar("Option deleted successfully");
          setDeleteDialogOpen(false);
          if (editingOption && editingOption.id === itemToDelete.data.id) {
            setEditingOption(null);
            setTempOption('');
          }
        } else {
          showSnackbar(response.data.message || "Failed to delete option", 'error');
        }
      }
    } catch (error) {
      console.error("Error during deletion:", error);
      showSnackbar(`Failed to delete ${itemToDelete.type}`, 'error');
    } finally {
      setIsDeleting(false);
      setItemToDelete(null);
    }
  };

  const handleNavigate = (category) => {
    // If it's an option-based category but has 1 or fewer options, 
    // we still go to the category-options page because it handles CMS now.
    // The main difference is the text displayed to the user.
    if (category.isOption) {
      navigate(`/category-options/${category.id}`);
    } else {
      navigate(`/cms-content/${category.id}`);
    }
  };

  return (
    <Container maxWidth="xl">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Categories
        </Typography>
        <Button variant="contained" sx={{ color: "white" }} color="primary" onClick={handleOpen}>
          Create Category
        </Button>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      ) : error ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh" flexDirection="column">
          <Typography variant="h6" color="error" gutterBottom>
            {error}
          </Typography>
          <Button variant="outlined" onClick={fetchCategories} startIcon={<CircularProgress size={16} sx={{ display: 'none' }} />}>
            Retry
          </Button>
        </Box>
      ) : categories.length === 0 ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <Typography variant="h6" color="textSecondary">
            No categories found. Start by creating a new category.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {categories.map((category) => (
            <Grid item size={{ xs: 12, sm: 6, md: 6, lg: 3 }} key={category.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardMedia
                  component="img"
                  height="100"
                  image={category.logo || "https://via.placeholder.com/300?text=No+Logo"}
                  alt={category.name}
                  sx={{ objectFit: 'contain', p: 2, bgcolor: '#f5f5f5' }}
                />
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Chip
                      label={category.isActive ? "Active" : "Inactive"}
                      color={category.isActive ? "success" : "error"}
                      size="small"
                    />
                  </Box>
                  <Typography gutterBottom variant="h6" component="div">
                    {category.name}
                  </Typography>
                </CardContent>
                <CardActions sx={{ justifyContent: 'space-between' }}>
                  <Button variant="contained" size="small" sx={{ color: "white" }} onClick={() => handleNavigate(category)}>
                    {
                      category.isOption && category.CategoryOptions?.length >= 1
                        ? 'View Options'
                        : 'View Content'
                    }
                  </Button>
                  <IconButton size="small" onClick={(e) => handleMenuOpen(e, category)}>
                    <MoreVertIcon />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEdit}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDelete}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>{editingId ? 'Edit Category' : 'Create New Category'}</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            name="logo"
            label="Service Icon"
            type="file"
            fullWidth
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            onChange={handleChange}
          />
          {previews.logo && (
            <Box mt={1} mb={2}>
              <img src={previews.logo} alt="Logo Preview" style={{ width: '100px', height: '100px', objectFit: 'contain' }} />
            </Box>
          )}
          <TextField
            autoFocus
            margin="dense"
            name="name"
            label="Service Name"
            type="text"
            fullWidth
            variant="outlined"
            value={newCategory.name}
            onChange={handleChange}
          />
          <TextField
            margin="dense"
            name="BoxText"
            label="Box Tag line"
            type="text"
            fullWidth
            variant="outlined"
            value={newCategory.BoxText}
            onChange={handleChange}
          />
          <TextField
            margin="dense"
            name="image"
            label="Service Image"
            type="file"
            fullWidth
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            onChange={handleChange}
          />
          {previews.image && (
            <Box mt={1} mb={2}>
              <img src={previews.image} alt="Preview" style={{ width: '100px', height: '100px', objectFit: 'contain' }} />
            </Box>
          )}

          <FormControlLabel
            control={
              <Switch
                checked={newCategory.isOption}
                onChange={handleChange}
                name="isOption"
                color="primary"
              />
            }
            label="Is Option?"
            sx={{ mt: 2 }}
          />

          {newCategory.isOption && (
            <Box mt={2} p={2} sx={{ bgcolor: '#f9f9f9', borderRadius: 1, border: '1px solid #eee' }}>
              <Typography variant="subtitle2" gutterBottom color="primary">
                Category Options
              </Typography>
              <Box display="flex" gap={1} mb={2}>
                <TextField
                  size="small"
                  label="Option Name"
                  placeholder="e.g. Basic, Premium..."
                  value={tempOption}
                  onChange={(e) => setTempOption(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddOption();
                    }
                  }}
                  fullWidth
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleAddOption}
                  disabled={isOptionActionLoading}
                  color={editingOption ? "secondary" : "primary"}
                >
                  {isOptionActionLoading ? <CircularProgress size={20} color="inherit" /> : (editingOption ? 'Update' : 'Add')}
                </Button>
              </Box>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {options.length === 0 ? (
                  <Typography variant="caption" color="textSecondary">
                    No options added yet.
                  </Typography>
                ) : (
                  options.map((option, index) => (
                    <Chip
                      key={index}
                      label={option.optionName || option.name}
                      onClick={() => handleStartEditOption(option)}
                      onDelete={() => handleDeleteOption(option)}
                      color={editingOption === option ? "secondary" : "primary"}
                      variant={editingOption === option ? "filled" : "outlined"}
                      size="small"
                      sx={{ cursor: 'pointer' }}
                    />
                  ))
                )}
              </Box>
            </Box>
          )}

          <FormControlLabel
            control={
              <Switch
                checked={newCategory.isActive}
                onChange={handleChange}
                name="isActive"
                color="primary"
              />
            }
            label="Active Status"
            sx={{ mt: 2, ml: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary" disabled={isSubmitting}>
            {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !isDeleting && setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this {itemToDelete?.type}? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button disableRipple disableElevation disableFocusRipple disableFocus onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button disableRipple disableElevation disableFocusRipple disableFocus
            onClick={confirmDelete}
            variant="contained"
            color="error"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Categories;
